import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getGameweekTransitionStatus } from "src/utils/gameweekTransitionApi";
import LockProgress from "./LockProgress";
import { pollIntervalMs } from "./lockProgressFormat";

const nf = new Intl.NumberFormat();

/** One metric: big number + label, coloured by severity. */
function Stat({ label, value, tone = "default", hint }) {
  const colour =
    tone === "error"
      ? "error.main"
      : tone === "warning"
      ? "warning.main"
      : tone === "success"
      ? "success.main"
      : "text.primary";
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography
        variant="h5"
        sx={{ color: colour, fontWeight: 700, lineHeight: 1.1 }}
      >
        {nf.format(value ?? 0)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {hint ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

function Group({ title, children }) {
  return (
    <Box sx={{ flex: "1 1 220px", minWidth: 220 }}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Stack
        direction="row"
        spacing={3}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 0.5 }}
      >
        {children}
      </Stack>
    </Box>
  );
}

const gwStatusColour = (status) =>
  ({ finalised: "success", live: "warning", locked: "secondary", open: "info" }[
    status
  ] ?? "default");

/**
 * "Gameweek transition" panel for the App Settings page: shows what the lock did
 * for the selected gameweek - transfers applied/failed, subs reverted, captaincy
 * settled, and whether any team was left with a negative budget.
 */
export default function GameweekTransitionCard() {
  const [status, setStatus] = useState(null);
  const [gameweekId, setGameweekId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The per-table numbers, toggles and rejection reasons live under "Details"
  // so the live lock progress is what an operator sees first.
  const [detailsOpen, setDetailsOpen] = useState(false);

  const load = useCallback(async (id, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await getGameweekTransitionStatus(id || undefined);
      setStatus(data);
      if (data?.gameweek?.id) setGameweekId(data.gameweek.id);
    } catch {
      setError("Unable to load gameweek transition status.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-refresh so the card tracks a lock live without a manual refresh. Silent
  // so the numbers update in place rather than flashing the spinner each tick.
  // Polls every 3 s while a lock is in progress, every 10 s otherwise.
  const interval = pollIntervalMs(status?.lockProgress);
  useEffect(() => {
    const timer = setInterval(
      () => void load(gameweekId || undefined, { silent: true }),
      interval
    );
    return () => clearInterval(timer);
  }, [load, gameweekId, interval]);

  const gw = status?.gameweek;
  const t = status?.transfers;
  const s = status?.substitutions;
  const c = status?.captaincy;
  const tog = status?.toggles;
  const rej = status?.rejections;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">Gameweek transition</Typography>
              {gw ? (
                <Chip
                  size="small"
                  color={gwStatusColour(gw.status)}
                  label={`GW${gw.number} · ${gw.status}`}
                />
              ) : null}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              What the lock did for this gameweek - live from the transfer,
              substitution, captaincy and budget tables.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="gw-transition-select">Gameweek</InputLabel>
              <Select
                labelId="gw-transition-select"
                label="Gameweek"
                value={gameweekId}
                onChange={(event) => {
                  setGameweekId(event.target.value);
                  void load(event.target.value);
                }}
              >
                {(status?.recentGameweeks ?? []).map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    GW{g.number} ({g.status})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => void load(gameweekId)}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={26} />
          </Stack>
        ) : !gw ? (
          <Alert severity="info">
            No gameweek has locked yet in the active season.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {status.lockProgress ? (
              <>
                <LockProgress progress={status.lockProgress} />
                <Divider />
              </>
            ) : null}

            <Box>
              <Button
                size="small"
                color="inherit"
                data-testid="gw-transition-details-toggle"
                aria-expanded={detailsOpen}
                startIcon={
                  detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />
                }
                onClick={() => setDetailsOpen((open) => !open)}
              >
                {detailsOpen ? "Hide details" : "Details"}
              </Button>
            </Box>

            <Collapse in={detailsOpen} unmountOnExit>
              <Stack spacing={2} data-testid="gw-transition-details">
                <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                  <Group title="Transfers">
                    <Stat label="applied" value={t.applied} tone="success" />
                    <Stat
                      label="failed"
                      value={t.failed}
                      tone={t.failed > 0 ? "warning" : "default"}
                    />
                    <Stat
                      label="corrupted"
                      value={t.committedThenFailed}
                      tone={t.committedThenFailed > 0 ? "error" : "success"}
                      hint="applied then reversed"
                    />
                    <Stat
                      label="pending"
                      value={t.pending}
                      tone={t.pending > 0 ? "warning" : "default"}
                      hint="not yet drained"
                    />
                    <Stat label="superseded" value={t.superseded} />
                    <Stat label="undone" value={t.undone} />
                    <Stat label="teams" value={t.teams} />
                  </Group>

                  <Group
                    title={`Substitutions${
                      status?.previousGameweek
                        ? ` (GW${status.previousGameweek.number})`
                        : ""
                    }`}
                  >
                    <Stat label="reverted" value={s.reverted} tone="success" />
                    <Stat
                      label="not reverted"
                      value={s.notReverted}
                      tone={s.notReverted > 0 ? "warning" : "default"}
                    />
                    <Stat label="total" value={s.total} />
                  </Group>

                  <Group title="Captaincy">
                    <Stat label="applied" value={c.applied} tone="success" />
                    <Stat
                      label="failed"
                      value={c.failed}
                      tone={c.failed > 0 ? "warning" : "default"}
                    />
                    <Stat
                      label="pending"
                      value={c.pending}
                      tone={c.pending > 0 ? "warning" : "default"}
                    />
                    <Stat label="total" value={c.total} />
                  </Group>

                  <Group title="Health">
                    <Stat
                      label="negative budgets"
                      value={status.health.negativeBudgetTeams}
                      tone={
                        status.health.negativeBudgetTeams > 0
                          ? "error"
                          : "success"
                      }
                      hint="teams below £0"
                    />
                  </Group>
                </Stack>

                <Divider />

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography variant="overline" color="text.secondary">
                    Gameplay toggles now
                  </Typography>
                  {[
                    ["Transfers", tog.transfersEnabled],
                    ["Substitutions", tog.substitutionsEnabled],
                    ["Captaincy", tog.captaincyChangesEnabled],
                    ["Updating pop-up", tog.teamTransitionPopupEnabled],
                  ].map(([label, on]) => (
                    <Chip
                      key={label}
                      size="small"
                      color={on ? "success" : "default"}
                      variant={on ? "filled" : "outlined"}
                      label={`${label}: ${on ? "on" : "off"}`}
                    />
                  ))}
                </Stack>

                {status.autoFinish ? (
                  <Typography variant="caption" color="text.secondary">
                    {status.autoFinish.completedAt
                      ? `Finished automatically at ${new Date(
                          status.autoFinish.completedAt
                        ).toLocaleString(
                          "en-GB"
                        )}: gameplay switched back on and the updating pop-up off.`
                      : `Auto-finish armed: pricing ${
                          status.autoFinish.pricingStatus ?? "not recorded yet"
                        }. Gameplay switches back on (and the pop-up off) once every phase above is done; App Settings remains the manual override.`}
                  </Typography>
                ) : null}

                {rej &&
                (rej.transfers.length > 0 || rej.captaincy.length > 0) ? (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        Rejection reasons
                      </Typography>
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {rej.transfers.map((r) => (
                          <Typography
                            key={`t-${r.reason}`}
                            variant="body2"
                            color="text.secondary"
                          >
                            <b>{nf.format(r.count)}</b> transfer
                            {r.count === 1 ? "" : "s"} - {r.reason}
                          </Typography>
                        ))}
                        {rej.captaincy.map((r) => (
                          <Typography
                            key={`c-${r.reason}`}
                            variant="body2"
                            color="text.secondary"
                          >
                            <b>{nf.format(r.count)}</b> captaincy - {r.reason}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  </>
                ) : null}
              </Stack>
            </Collapse>

            {status.generatedAt ? (
              <Typography variant="caption" color="text.secondary">
                As of {new Date(status.generatedAt).toLocaleString()}
              </Typography>
            ) : null}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
